import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { getPublicPath } from "../utils/seoPages";
import { trackEvent } from "../utils/analytics";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const courseIdParam = params.get("course_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const [courseId, setCourseId] = useState(courseIdParam || "");
  const purchaseTracked = React.useRef(false);
  const { settings } = useSettings();
  const catalogPath = getPublicPath(settings, "/catalog");

  useEffect(() => {
    let cancelled = false;
    if (!sessionId && courseIdParam) {
      setStatus("paid");
      setCourseId(courseIdParam);
      return;
    }
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let count = 0;
    const poll = async () => {
      if (cancelled || count >= 8) return;
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Conversion GA4 : achat confirmé (une seule fois)
          if (!purchaseTracked.current) {
            purchaseTracked.current = true;
            trackEvent("purchase", {
              transaction_id: sessionId,
              value: (data.amount_total || 0) / 100,
              currency: (data.currency || "eur").toUpperCase(),
            });
          }
          setStatus("paid");
          setCourseId(data?.metadata?.course_id || "");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {}
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, courseIdParam]);

  return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="checkout-success-page">
      {status === "polling" && (
        <>
          <div className="animate-spin h-12 w-12 border-4 border-brand-green border-t-transparent rounded-full mx-auto" />
          <h1 className="font-heading text-2xl font-black mt-6">Vérification du paiement...</h1>
          <p className="text-sm text-gray-500 mt-2">Tentative {attempts}/8</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-20 h-20 text-brand-green mx-auto" />
          <h1 className="font-heading text-3xl font-black mt-4">Paiement confirmé !</h1>
          <p className="text-gray-600 mt-2">Tu as maintenant accès à ta formation. Bienvenue !</p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/dashboard" className="btn-primary" data-testid="success-go-dashboard">
              Mon espace
            </Link>
            {courseId && <Link to={`/formations/${courseId}`} className="btn-ghost" data-testid="success-go-course">Voir la formation</Link>}
          </div>
        </>
      )}
      {(status === "error" || status === "expired") && (
        <>
          <AlertCircle className="w-20 h-20 text-red-500 mx-auto" />
          <h1 className="font-heading text-2xl font-black mt-4">Paiement non confirmé</h1>
          <p className="text-gray-600 mt-2">Si tu as été débité, vérifie ton dashboard ou contacte-nous.</p>
          <Link to={catalogPath} className="btn-primary mt-6 inline-flex">Retour au catalogue</Link>
        </>
      )}
    </div>
  );
}
